import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { categoryService } from "../services";
import { Formik, Form } from "formik";
import { categoryValidationSchema } from "../utils/validationSchemas";

const CategoryForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [parentCategories, setParentCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  // Get category data from location state for editing
  const categoryData = location.state?.category || null;
  const isEdit = !!categoryData;

  useEffect(() => {
    fetchParentCategories();
    if (categoryData?.image) {
      setImagePreview(`${process.env.REACT_APP_UPLOAD_URL}/${categoryData.image}`);
    }
  }, []);

  const fetchParentCategories = async () => {
    try {
      const response = await categoryService.getParentCategories();
      setParentCategories(response);
    } catch (error) {
      console.error("Failed to fetch parent categories:", error);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      console.log(values, "this is values");
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("isActive", values.isActive);

      if (values.parentCategoryId) {
        formData.append("parentCategoryId", values.parentCategoryId);
      }

      if (values.image && values.image instanceof File) {
        formData.append("image", values.image);
      } else if (isEdit && !values.image) {
        // Keep existing image if no new image is selected
        formData.append("existingImage", categoryData.image || "");
      }

      if (isEdit) {
        await categoryService.updateCategory(categoryData._id, formData);
        toast.success("Category updated successfully");
      } else {
        await categoryService.createCategory(formData);
        toast.success("Category created successfully");
      }

      navigate("/categories");
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "create"} category`
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/categories");
  };

  const handleImageChange = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      setFieldValue("image", file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (setFieldValue) => {
    setFieldValue("image", null);
    setImagePreview(null);
  };

  const initialValues = {
    title: categoryData?.title || "",
    description: categoryData?.description || "",
    parentCategoryId: categoryData?.parentCategoryId?._id || "",
    isActive: categoryData?.isActive ?? true,
    image: null,
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Back to Categories
        </Button>
        <Typography variant="h4">
          {isEdit ? "Edit Category" : "Create New Category"}
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={categoryValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Category Title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.isActive}
                        onChange={(e) =>
                          setFieldValue("isActive", e.target.checked)
                        }
                        disabled={loading}
                      />
                    }
                    label="Active"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    multiline
                    rows={4}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Parent Category (Optional)</InputLabel>
                    <Select
                      name="parentCategoryId"
                      value={values.parentCategoryId}
                      onChange={(e) =>
                        setFieldValue("parentCategoryId", e.target.value)
                      }
                      onBlur={handleBlur}
                      disabled={loading}
                    >
                      <MenuItem value="">
                        <em>None (Top-level category)</em>
                      </MenuItem>
                      {parentCategories.map((category) => (
                        <MenuItem key={category._id} value={category._id}>
                          {category.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Category Image
                    </Typography>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="category-image-upload"
                      type="file"
                      onChange={(e) => handleImageChange(e, setFieldValue)}
                      disabled={loading}
                    />
                    <label htmlFor="category-image-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<ImageIcon />}
                        disabled={loading}
                      >
                        Upload Image
                      </Button>
                    </label>
                  </Box>
                </Grid>

                {imagePreview && (
                  <Grid item xs={12}>
                    <Card sx={{ maxWidth: 300 }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={imagePreview}
                        alt="Category preview"
                      />
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="text.secondary">
                            Preview
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveImage(setFieldValue)}
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {values.parentCategoryId
                      ? "This will be created as a subcategory."
                      : "This will be created as a top-level category."}
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        loading ? <CircularProgress size={20} /> : <SaveIcon />
                      }
                      disabled={loading || isSubmitting}
                    >
                      {loading
                        ? "Saving..."
                        : isEdit
                        ? "Update Category"
                        : "Create Category"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default CategoryForm;
